// src/layouts/TopBar/JarvislyCreditsWidget.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { useState, useCallback } from 'react';
import {
  Card,
  Button,
  Modal,
  Radio,
  Space,
  Typography,
  Flex,
  Divider,
  Tag,
  Spin,
  theme,
} from 'antd';
import {
  DollarOutlined,
  PlusOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import marketplaceService from 'services/marketplaceService';
import { API } from 'constants/Api';
import { onProduction } from 'utils/helpers';

const { Text, Title } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// Credit packages available for purchase
const CREDIT_PACKAGES = [
  { id: 'pack_10', credits: 10, price: 0, popular: true },
  // { id: 'pack_50', credits: 50, price: 5.0, popular: false },
  { id: 'pack_100', credits: 100, price: 0, popular: false, discount: 5 },
  // { id: 'pack_250', credits: 250, price: 22.5, popular: true, discount: 10 },
  // { id: 'pack_500', credits: 500, price: 42.5, popular: false, discount: 15 },
  // { id: 'pack_1000', credits: 1000, price: 80.0, popular: false, discount: 20 },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************

const JarvislyCreditsWidget = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const currentBalance = storedSubscription?.jarvislyCredits?.balance || 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('pack_10');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleRefreshBalance = useCallback(async () => {
    if (isLoadingBalance) return;
    setIsLoadingBalance(true);
    await marketplaceService.refreshCreditsBalance();
    setIsLoadingBalance(false);
  }, [isLoadingBalance]);

  const handleOpenPurchaseModal = useCallback(() => {
    setShowPurchaseModal(true);
  }, []);

  const handleClosePurchaseModal = useCallback(() => {
    if (!isPurchasing) {
      setShowPurchaseModal(false);
      setSelectedPackage('pack_10');
    }
  }, [isPurchasing]);

  const handlePurchaseCredits = useCallback(async () => {
    const pkg = CREDIT_PACKAGES.find(p => p.id === selectedPackage);
    if (!pkg) return;

    setIsPurchasing(true);

    try {
      const [response] = await axiosService.post({
        url: API.MARKETPLACE.PURCHASE_CREDITS,
        data: {
          packageId: pkg.id,
          credits: pkg.credits,
          amount: pkg.price,
          type: 'prepaid',
        },
        returnAsObject: true,
      });

      if (response?.success) {
        const newBalance = response.newBalance || currentBalance + pkg.credits;

        // Use global refresh function to update Zustand store
        await marketplaceService.refreshCreditsBalance();

        appService.notification({
          flag: 'success',
          icon: <DollarOutlined style={{ color: '#52c41a' }} />,
          title: 'credits_purchased_successfully',
          description: translateX('credits_added_to_balance', {
            credits: <strong>{pkg.credits}</strong>,
            newBalance: <strong>{newBalance.toLocaleString()}</strong>,
          }),
          duration: 6,
        });

        setShowPurchaseModal(false);
        setSelectedPackage('pack_10');
      } else {
        appService.notification({
          flag: 'error',
          title: 'purchase_failed',
          description: response?.message || 'error_processing_purchase',
        });
      }
    } catch (error) {
      console.error('[JarvislyCreditsWidget] Error purchasing credits:', error);
      appService.notification({
        flag: 'error',
        title: 'purchase_failed',
        description: 'error_processing_purchase',
      });
    } finally {
      setIsPurchasing(false);
    }
  }, [selectedPackage, currentBalance]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  const selectedPkg = CREDIT_PACKAGES.find(p => p.id === selectedPackage);

  return (
    <>
      {/* Credits Card */}
      <Card
        size="small"
        styles={{
          body: { padding: '12px 16px' },
        }}
      >
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={12} style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 8,
                backgroundColor: token.colorSuccessBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DollarOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  display: 'block',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {/*{translateX('jarvisly_credits')}*/}
              </Text>
              <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.2 }}>
                {isLoadingBalance ? <Spin size="small" /> : currentBalance.toLocaleString()}
              </div>
            </div>
          </Flex>

          <Space size="small">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleRefreshBalance}
              loading={isLoadingBalance}
            />
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleOpenPurchaseModal}
            >
              {translateX('add')}
            </Button>
          </Space>
        </Flex>
      </Card>

      {/* Purchase Modal */}
      <Modal
        title={
          <Flex align="center" gap={8}>
            <CreditCardOutlined />
            {translateX('purchase_credits')}
          </Flex>
        }
        open={showPurchaseModal}
        onCancel={handleClosePurchaseModal}
        okText={translateX('confirm_purchase')}
        cancelText={translateX('cancel')}
        onOk={handlePurchaseCredits}
        confirmLoading={isPurchasing}
        okButtonProps={{ disabled: onProduction() }}
        width={420}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          {/* Current Balance */}
          <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
            <Text type="secondary">{translateX('current_balance')}:</Text>
            <Text strong style={{ fontSize: 16 }}>
              {currentBalance.toLocaleString()} {translateX('credits')}
            </Text>
          </Flex>

          <Divider style={{ margin: '12px 0' }} />

          {/* Package Selection */}
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            {translateX('select_package')}:
          </Text>

          <Radio.Group
            value={selectedPackage}
            onChange={e => setSelectedPackage(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space orientation="vertical" style={{ width: '100%' }}>
              {CREDIT_PACKAGES.map(pkg => (
                <Radio
                  key={pkg.id}
                  value={pkg.id}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${selectedPackage === pkg.id ? token.colorPrimary : token.colorBorder}`,
                    borderRadius: 8,
                    backgroundColor:
                      selectedPackage === pkg.id ? token.colorPrimaryBg : 'transparent',
                  }}
                >
                  <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                    <Flex align="center" gap={8}>
                      <Text strong>{pkg.credits.toLocaleString()}</Text>
                      <Text type="secondary">{translateX('credits')}</Text>
                      {pkg.popular && (
                        <Tag color="green" style={{ margin: 0 }}>
                          {translateX('popular')}
                        </Tag>
                      )}
                      {pkg.discount && (
                        <Tag color="orange" style={{ margin: 0 }}>
                          -{pkg.discount}%
                        </Tag>
                      )}
                    </Flex>
                    <Text strong style={{ color: token.colorSuccess }}>
                      {financialService.formatCurrency(pkg.price)}
                    </Text>
                  </Flex>
                </Radio>
              ))}
            </Space>
          </Radio.Group>

          <Divider style={{ margin: '16px 0 12px' }} />

          {/* Summary */}
          {selectedPkg && (
            <Flex justify="space-between" align="center">
              <div>
                <Text type="secondary">{translateX('total')}:</Text>
                <div style={{ fontSize: 11, color: token.colorTextSecondary }}>
                  {translateX('new_balance')}:{' '}
                  {(currentBalance + selectedPkg.credits).toLocaleString()} {translateX('credits')}
                </div>
              </div>
              <Title level={4} style={{ margin: 0, color: token.colorSuccess }}>
                {financialService.formatCurrency(selectedPkg.price)}
              </Title>
            </Flex>
          )}

          {/* Simulation Notice */}
          <div
            style={{
              marginTop: 16,
              padding: '8px 12px',
              backgroundColor: token.colorInfoBg,
              borderRadius: 6,
              border: `1px solid ${token.colorInfoBorder}`,
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              <CheckCircleOutlined style={{ marginRight: 6, color: token.colorInfo }} />
              {translateX('purchase_simulation_notice')}
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************

export default JarvislyCreditsWidget;
